import React, { useEffect, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import {
  GenericTable,
  SearchableDropdown,
  RoleValidator,
} from "../../../Components/Common";
import { useStock } from "../Hooks/useStock";
import {
  getRNStatusColor,
  MATERIAL_COLUMN,
  MATERIAL_COLUMN_LABELS,
  RETURN_NOTE_STATUS,
  RETURN_NOTE_STATUS_LABELS,
  STOCK_MODAL_MODES,
} from "../constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DETAIL_RETURN_NOTE,
  EDIT,
  EDIT_RETURN_NOTE,
  RETURN_NOTE,
  VIEW,
} from "../../../routes";
import { MdModeEditOutline } from "react-icons/md";
import { useReturnNote } from "../Hooks/useReturnNote";
import { useWarehouse } from "../Hooks/useWarehouse";
import { useProductMaster } from "../Hooks/useProductMaster";
import { ROLES } from "../../../Constants/Strings";

export default function ReturnNoteCreate() {
  const { loading } = useProductMaster();
  const {
    loading: rnLoading,
    createReturnNote,
    updateReturnNote,
    fetchReturnNotesById,
  } = useReturnNote();
  const { fetchWarehouseProducts } = useStock();
  const { fetchWarehouses, setFilters, filters } = useWarehouse();
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [notes, setNotes] = useState("");
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();
  const [category, setCategory] = useState(null);
  const [make, setMake] = useState(null);
  const { getAllMakes, fetchCategories } = useProductMaster();

  const removeMaterial = (id) => {
    setMaterials((prev) => prev.filter((item) => item.id !== id));
  };

  const handleInputChange = (id, field, value) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleAddMaterial = (material) => {
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) return prev; // skip adding duplicate
      return [
        ...prev,
        { ...material, product: material.id, return_quantity: 1 },
      ];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div className="flex flex-col">
            <span>{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM ? row.UOM : "No UOM"})
            </span>
          </div>
        );
      case "return_quantity":
        return (
          <>
            {mode === STOCK_MODAL_MODES.VIEW ? (
              <span>{row.return_quantity}</span>
            ) : (
              <input
                type="number"
                min="1"
                value={row.return_quantity || ""}
                onChange={(e) =>
                  handleInputChange(row.id, "return_quantity", e.target.value)
                }
                className="w-24 text-center border border-gray-300 rounded-lg px-2 py-1 
                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                 transition-all duration-150 outline-none shadow-sm"
                placeholder="Qty"
                disabled={mode === STOCK_MODAL_MODES.VIEW}
              />
            )}
          </>
        );
      case "comments":
        return (
          <>
            {mode === STOCK_MODAL_MODES.VIEW ? (
              <span>{row.comments || ""}</span>
            ) : (
              <textarea
                value={row.comments || ""}
                onChange={(e) =>
                  handleInputChange(row.id, "comments", e.target.value)
                }
                placeholder="Enter comments..."
                // rows={3} // adjust height
                className={`w-full border-b border-gray-300 rounded-lg px-2 py-1 bg-transparent
              focus:border-blue-500 focus:ring-0 outline-none
              resize-none transition-colors duration-150`}
                disabled={mode === STOCK_MODAL_MODES.VIEW}
              />
            )}
          </>
        );
      case "actions":
        return (
          <>
            <RoleValidator
              allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            >
              <button
                className=" hover:underline ml-2"
                onClick={() => removeMaterial(row.id)}
              >
                <Trash2 className="h-5 w-5 text-red-400" />
              </button>
            </RoleValidator>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleSubmit = async (status) => {
    if (!warehouseId) {
      alert("Please select a warehouse.");
      return;
    }
    if (materials.length === 0) {
      alert("Please add at least one material.");
      return;
    }

    const payload = { warehouse: warehouseId, notes, materials, status };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await createReturnNote(payload);
    } else {
      data = await updateReturnNote(param.instanceId, payload);
      setMaterials(data.materials);
      setNotes(data.notes);
      setWarehouse(data.warehouse_name);
      setWarehouseId(data.warehouse);
      setStatus(data.status);
    }
    navigate(DETAIL_RETURN_NOTE.replace(":instanceId", data.id));
  };

  const getReturnNoteData = async () => {
    const data = await fetchReturnNotesById(param.instanceId);
    setMaterials(data.materials);
    setNotes(data.notes);
    setWarehouse(data.warehouse_name);
    setWarehouseId(data.warehouse);
    setStatus(data.status);
  };

  useEffect(() => {
    if (param.instanceId) {
      getReturnNoteData();

      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
    }
  }, [param.instanceId, location.pathname]);

  if (rnLoading) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <div className="animate-spin border-t h-7 w-7 rounded-full border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] p-2">
      <div className="flex justify-between items-center">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 m-4">
          <button
            type="button"
            onClick={() => navigate(RETURN_NOTE)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode} Return Note
        </h2>
        {mode === STOCK_MODAL_MODES.VIEW &&
          status === RETURN_NOTE_STATUS.DRAFT && (
            <RoleValidator
              allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            >
              <button
                className="mb-4 flex gap-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary transition"
                onClick={() =>
                  navigate(
                    EDIT_RETURN_NOTE.replace(":instanceId", param.instanceId)
                  )
                }
              >
                <MdModeEditOutline size={20} />
              </button>
            </RoleValidator>
          )}
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white p-6 md:border-r-2">
          <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
            Return Note
          </h2>

          {/* Warehouse Dropdown */}
          <SearchableDropdown
            label="Site"
            fetchFunction={(param) =>
              fetchWarehouses({ ...param, is_site: true })
            }
            onSelect={(val) => {
              setFilters({ ...filters, warehouse: val });
              setWarehouseId(val);
            }}
            valueKey="id"
            labelKey="name"
            placeholder={warehouse ? warehouse : "Select Warehouse"}
            disabled={mode !== STOCK_MODAL_MODES.ADD}
          />

          {/* Notes Field */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium my-2">
              Notes
            </label>
            <textarea
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>
          {mode !== STOCK_MODAL_MODES.ADD && status && (
            <div>
              <span
                className={`py-2 px-3 font-semibold rounded-lg ${getRNStatusColor(
                  status
                )}`}
              >
                {RETURN_NOTE_STATUS_LABELS[status]}
              </span>
            </div>
          )}
        </div>

        {/* Right Side — Selected Materials */}
        <div className="px-2 py-6">
          <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
            Selected Materials
          </h2>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <div className="flex flex-col gap-4">
                <div className="flex flex-row justify-between gap-4">
                  {/* Category Dropdown */}
                  <div className="w-1/2">
                    <SearchableDropdown
                      label="Category"
                      fetchFunction={fetchCategories}
                      onSelect={setCategory}
                      value={category}
                      labelKey="name"
                      placeholder="Select Category"
                      includeAllOption={true}
                      allOptionLabel="No Category"
                    />
                  </div>
                  {/* Make Dropdown */}
                  <div className="w-1/2">
                    <SearchableDropdown
                      label="Make"
                      fetchFunction={(params) =>
                        getAllMakes({ ...params, category: category?.id })
                      }
                      onSelect={setMake}
                      value={make}
                      labelKey="make"
                      placeholder="Select Make"
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div>
                    <SearchableDropdown
                      key={warehouseId}
                      label="Add Product"
                      fetchFunction={(param) =>
                        fetchWarehouseProducts({
                          warehouseId,
                          search: param?.search,
                          categoryId: category?.id,
                          make: make?.make,
                        })
                      }
                      onSelect={(material) =>
                        handleAddMaterial({
                          id: material.id,
                          product: material.id,
                          product_name: material.product_name,
                          UOM: material.UOM,
                          specification: material.specification,
                          available_quantity: material.available_quantity,
                          return_quantity: 1,
                        })
                      }
                      labelKey="product_name"
                      subLabelKey="specification"
                      showSubLabel={true}
                      placeholder={
                        warehouseId
                          ? "Select Product"
                          : "Select Warehouse first"
                      }
                      disabled={!warehouseId || mode === STOCK_MODAL_MODES.VIEW}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg">
              No materials selected yet.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMN.filter((col) => col !== "actions")
                      : MATERIAL_COLUMN
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_COLUMN_LABELS}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-3">
            {/* Site Engineer or Project Engineer — Draft or Add */}
            {mode !== STOCK_MODAL_MODES.VIEW && (
              <div className="flex justify-end gap-4 mt-6">
                <RoleValidator
                  allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
                >
                  <button
                    onClick={() => handleSubmit(RETURN_NOTE_STATUS.DRAFT)}
                    className={
                      "px-4 font-medium py-2 rounded-lg transition-all my-2 bg-gray-200 text-gray-800 hover:bg-gray-300"
                    }
                  >
                    Save as Draft
                  </button>
                </RoleValidator>

                <RoleValidator
                  allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
                >
                  <button
                    onClick={() =>
                      handleSubmit(RETURN_NOTE_STATUS.PENDING_FOR_APPROVAL)
                    }
                    className="px-4 text-white font-medium py-2 rounded-lg transition-all my-2 bg-primary hover:bg-blue-700"
                  >
                    Submit For Approval
                  </button>
                </RoleValidator>
              </div>
            )}

            {/* Project Manager — Approve / Reject */}
            {mode === STOCK_MODAL_MODES.VIEW &&
              status === RETURN_NOTE_STATUS.PENDING_FOR_APPROVAL && (
                <div className="flex justify-end gap-4 mt-6">
                  <RoleValidator allowedRoles={[ROLES.PROJECT_MANAGER]}>
                    <button
                      onClick={() => handleSubmit(RETURN_NOTE_STATUS.APPROVED)}
                      className="px-4 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg"
                    >
                      Approve
                    </button>
                  </RoleValidator>
                  <RoleValidator allowedRoles={[ROLES.PROJECT_MANAGER]}>
                    <button
                      onClick={() => handleSubmit(RETURN_NOTE_STATUS.REJECTED)}
                      className="px-4 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg"
                    >
                      Reject
                    </button>
                  </RoleValidator>
                </div>
              )}

            {/* Site Engineer or Project Engineer — Post Approval Transit Flow */}
            {mode === STOCK_MODAL_MODES.VIEW &&
              status === RETURN_NOTE_STATUS.APPROVED && (
                <div className="flex justify-end gap-4 mt-6">
                  <RoleValidator
                    allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(RETURN_NOTE_STATUS.RETURN_INITIATED)
                      }
                      className="px-4 bg-primary hover:bg-primary text-white py-2 rounded-lg"
                    >
                      Initiate Return
                    </button>
                  </RoleValidator>
                </div>
              )}

            {mode === STOCK_MODAL_MODES.VIEW &&
              status === RETURN_NOTE_STATUS.RETURN_INITIATED && (
                <div className="flex justify-end gap-3 mt-6">
                  <RoleValidator
                    allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(RETURN_NOTE_STATUS.RETURN_COMPLETED)
                      }
                      className="px-4 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg"
                    >
                      Return Completed
                    </button>
                  </RoleValidator>
                  <RoleValidator
                    allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(RETURN_NOTE_STATUS.RETURN_FAILED)
                      }
                      className="px-4 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg"
                    >
                      Return Failed
                    </button>
                  </RoleValidator>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
